import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ChevronRight, CircleDot, Circle } from "lucide-react";

const SLUGS = [
  { slug: "site", label: "Site (Header, Footer, Global)", desc: "Logo, nav, contact info, social links, marquee" },
  { slug: "home", label: "Home", desc: "Hero, intro, experiences, weekly specials, slider" },
  { slug: "steakhouse", label: "Steakhouse", desc: "Hero, intro, interior, slider" },
  { slug: "tavern", label: "Tavern", desc: "Hero, intro, patio, specials, slider" },
  { slug: "venue", label: "Venue", desc: "Hero, intro, features, real weddings, slider" },
  { slug: "gallery", label: "Gallery", desc: "Hero + gallery images" },
  { slug: "contact", label: "Contact", desc: "Address, phone, email, hours, map" },
  { slug: "careers", label: "Careers", desc: "Hero, perks, departments & roles, application form" },
];

const PagesList = () => {
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/cms/pages");
        const map = {};
        data.forEach((p) => (map[p.slug] = p));
        setPages(map);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );

  const isDirty = (p) => {
    if (!p) return false;
    try {
      return JSON.stringify(p.draft || {}) !== JSON.stringify(p.published || {});
    } catch {
      return false;
    }
  };

  return (
    <div data-testid="pages-list">
      <h1 className="text-2xl font-medium text-zinc-900">Pages</h1>
      <p className="text-sm text-zinc-500 mt-1">Edit any page. Changes save as a draft until you publish.</p>

      <ul className="mt-8 divide-y divide-zinc-200 border border-zinc-200 rounded-lg overflow-hidden bg-white">
        {SLUGS.map(({ slug, label, desc }) => {
          const dirty = isDirty(pages[slug]);
          return (
            <li key={slug}>
              <Link
                to={`/admin/pages/${slug}`}
                data-testid={`page-row-${slug}`}
                className="flex items-center justify-between gap-4 px-6 py-5 hover:bg-zinc-50 transition-colors"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-900">{label}</span>
                    {dirty ? (
                      <span data-testid={`status-${slug}-draft`} className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                        <CircleDot size={10} /> Unpublished
                      </span>
                    ) : (
                      <span data-testid={`status-${slug}-published`} className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        <Circle size={10} /> Published
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{desc}</p>
                </div>
                <ChevronRight className="text-zinc-400" size={18} />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PagesList;
